import { forwardRef, useEffect, useLayoutEffect, useState, useCallback, memo, type ReactNode } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { useSubNavStore } from '@frontend/nav-primitives';
import { usePanelStore } from '@frontend/panel-engine';
import { OnboardingGuide } from '@frontend/product-onboarding';
import { useNavSize } from '@frontend/responsiveness';
import { StaticSettingsRoutes } from '@frontend/settings-routing';
import { useShellTheme } from '@frontend/shell-theme';
import { AnyRoute, Link } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Heading, ModalBackdrop, NakedUl, Button, NotificationBadge } from '@frontend/design-system';
import { BubbleNav } from './primitives/bubble-nav';
import { PendoTrigger } from './primitives/pendo-trigger';
import { SettingsNavButton } from './primitives/settings-button';
import { useNav } from './providers/nav-provider';
import { cssVariables, fullOpenStyle, slimOpenStyle } from './styles';

type NavProps = {
  children: React.ReactNode;
};

const MotionNakedUl = motion.create(NakedUl);
const MotionModalBackdrop = motion.create(ModalBackdrop);

const hasSameRootPath = (a: string, b: string): boolean => {
  if (!a || !b) return false;
  const [, aRoot] = a.split('/');
  const [, bRoot] = b.split('/');
  return aRoot === bRoot;
};

const hasSameSubPath = (a: string, b: string): boolean => {
  const aPath = a.split('/');
  const bPath = b.split('/');
  return aPath[2] === bPath[1];
};

const mobileNavContentStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'column',
  width: '100%',
  borderTop: `1px solid ${theme.colors.neutral20}`,
});

const expandButtonStyles = css`
  position: absolute;
  right: 2px;
  height: 100%;
`;

const expandButtonInnerStyles = css`
  width: 32px;
  border: none;
  height: 100%;
  padding-top: 20px;
  align-items: flex-start;
  border-radius: 0 8px 0 0;

  &:hover {
    background-color: ${theme.colors.neutral5};
    color: ${theme.colors.icon.select};
    svg {
      fill: ${theme.colors.icon.select};
      color: ${theme.colors.icon.select};
    }
  }
`;

const headingStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: calc(1.25 * var(--nav-item-font-size));
  margin: 0.3333em 0;
  padding: 5px 8px 0 8px;
`;

const listStyles = css`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  margin: 0.5em 0 0;
`;

const regularSubnavBaseStyles = css`
  background-color: var(--colors-white);
  display: flex;
  flex-direction: column;
  font-size: var(--nav-item-font-size);
  height: 100%;
  left: calc(var(--nav-icon-width) - 50px);
  opacity: 0;
  position: absolute;
  transition: left 0.3s ease-in-out, opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  visibility: hidden;
  overflow: auto;
  width: 100%;
`;

const regularSubnavActiveStyles = css`
  left: var(--nav-icon-width);
  opacity: 1;
  visibility: visible;
`;

const subnavFlyoutBaseStyles = css`
  background-color: var(--colors-white);
  display: flex;
  flex-direction: column;
  font-size: var(--nav-item-font-size);
  gap: 0.5em;
  height: 100%;
  left: var(--nav-icon-width);
  overflow: hidden;
  position: absolute;
  top: 0;
`;

const mobileNavStyles = (open: boolean, isSubMenu: boolean) => [
  cssVariables(true),
  css`
    background: var(--colors-white);
    display: flex;
    height: 100%;
    left: 0;
    position: absolute;
    width: var(--nav-width);
    z-index: ${theme.zIndex.overlay};
  `,
  !open &&
    css`
      left: ${isSubMenu
        ? 'calc(calc(var(--nav-width) + var(--nav-icon-width)) * -1)'
        : 'calc((var(--nav-width) * -1))'};
    `,
];

const mobileNavListStyles = (sideNavColor: string) => css`
  background: ${sideNavColor};
  padding-top: ${theme.spacing(1)};
  height: 100%;
  margin: 0;
  width: 100%;
  overflow: auto;
`;

const desktopNavStyles = (isSlimModeActive: boolean, parentLink: string, colorObject: any) => [
  cssVariables(),
  isSlimModeActive
    ? css`
        width: var(--nav-slim-width);
        z-index: 30;
      `
    : css`
        width: var(--nav-width);
      `,
  css`
    display: flex;
    height: 100%;
    position: relative;
    transition: left 300ms ease-in-out;

    p,
    ul {
      margin: 0;
    }

    :hover .wv-nav-main__desktop button[data-path='${parentLink}'] {
      background-color: ${colorObject.hover};
    }

    .wv-nav-subnav-flyout__desktop {
      transition: width 0.3s ease-in-out, left 0.3s ease-in-out;
    }
  `,
];

const desktopNavListStyles = (isSlimModeActive: boolean, open: boolean) => css`
  padding-top: ${theme.spacing(1)};
  border-radius: ${isSlimModeActive && open ? 0 : '0 0.5em 0 0'};
  height: 100%;
  overflow: auto;
`;

const subnavItemBaseStyles = css`
  text-decoration: none;
  align-items: center;
  text-align: left;
  border-radius: var(--border-radius-medium);
  display: flex;
  font-size: var(--nav-item-font-size);
  min-height: 2.5em;
  justify-content: space-between;
  padding: 0.5em 1em;
  gap: 1em;
  line-height: 1.5em;
  font-weight: ${theme.font.weight.regular};
  color: ${theme.font.colors.default};
  width: 100%;
  position: relative;
  transition: background-color 0.3s ease-in-out;
  white-space: normal;
  :hover {
    background-color: var(--colors-neutral-5);
  }
`;

const subnavLabelStyles = css`
  flex: 1 1 auto;
  min-width: 0;
`;

const subnavItemActiveStyles = css`
  background-color: var(--nav-background-color);
  font-weight: ${theme.font.weight.bold};
`;

const passiveBadgeStyles = css`
  background: transparent;
  border-radius: none;
  color: ${theme.colors.neutral50};
`;

const ExpandButton = memo(({ onClick }: { onClick: () => void }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1, transition: { duration: 0, delay: 0.25 } }}
    exit={{ opacity: 0, transition: { duration: 0 } }}
    css={expandButtonStyles}
  >
    <Button variant='secondary' iconName='collapse-right' css={expandButtonInnerStyles} onClick={onClick} />
  </motion.div>
));

ExpandButton.displayName = 'ExpandButton';

const Backdrop = ({ onClick }: { onClick?: () => void }) => (
  <MotionModalBackdrop
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3 }}
    omitOffset
    zIndex={1}
    onClick={onClick}
  />
);

Backdrop.displayName = 'Backdrop';

const RegularSubnav = memo(() => {
  const parentLink = useNav((state) => state.parentLink);
  const isSubMenu = useNav((state) => state.isSubMenu);

  return (
    <section
      tabIndex={0}
      className='wv-nav-subnav-flyout__desktop'
      style={{ top: theme.spacing(5), height: `calc(100% - ${theme.spacing(5)})`, width: '100%' }}
      data-path={parentLink}
      css={[regularSubnavBaseStyles, isSubMenu && regularSubnavActiveStyles]}
    >
      <SubmenuContent />
    </section>
  );
});

RegularSubnav.displayName = 'RegularSubnav';

const SubnavItem = memo(
  ({
    label,
    count,
    isPassiveCount,
    relativePath,
    path,
    trackingId,
    parentLink,
    isActive,
    onNavigate,
  }: {
    label: ReactNode;
    count?: number;
    isPassiveCount?: boolean;
    relativePath?: string;
    path?: string;
    trackingId: string;
    parentLink: string;
    isActive: boolean;
    onNavigate: (pathLink: string) => void;
  }) => {
    const fullPath = path || `${parentLink}${relativePath}`;

    const handleClick = useCallback(() => {
      onNavigate(fullPath);
    }, [fullPath, onNavigate]);

    return (
      <li key={relativePath || path}>
        <Link
          data-trackingid={trackingId}
          to={fullPath as AnyRoute}
          preload={'intent'}
          onClick={handleClick}
          css={[subnavItemBaseStyles, isActive && subnavItemActiveStyles]}
        >
          <span css={subnavLabelStyles}>{label}</span>
          {count ? (
            <NotificationBadge css={isPassiveCount && passiveBadgeStyles} truncateCount>
              {count}
            </NotificationBadge>
          ) : null}
        </Link>
      </li>
    );
  }
);

SubnavItem.displayName = 'SubnavItem';

export const SubmenuContent = memo(() => {
  const toggleNav = useNav((state) => state.toggleNav);
  const isSlimModeActive = useNav((state) => state.isSlimModeActive);
  const isOpen = useNav((state) => state.open);
  const parentLink = useNav((state) => state.parentLink);
  const currentPath = useNav((state) => state.currentPathname);
  const selectedParent = useNav((state) => state.selectedParent);
  const navigate = useNav((state) => state.navigate);

  const parentNavData = useSubNavStore((state) => {
    // When parentLink points to a nav section that has been unregistered (e.g. disabled
    // in workspace scope), fall back to selectedParent so the subnav shows the correct
    // section for the current route instead of rendering blank.
    return state.parentNavData?.[parentLink] ?? state.parentNavData?.[selectedParent];
  });

  const { subNavItems = [], label: parentLabel, settings } = parentNavData || {};

  const handleNavigate = useCallback(
    (path: string) => {
      toggleNav();
      setTimeout(() => {
        navigate?.(path);
      }, 350);
    },
    [toggleNav, navigate]
  );

  const submenuOpacityStyles = isSlimModeActive
    ? isOpen
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0;
          pointer-events: none;
        `
    : undefined;

  return (
    <div
      css={[
        {
          padding: '0 0.5em',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          height: '100%',
          transition: 'opacity 0.3s ease-in-out',
        },
        submenuOpacityStyles,
      ]}
    >
      <div css={{ width: '100%' }}>
        <Heading level={3} css={headingStyles}>
          <span>{parentLabel}</span>
          {isSlimModeActive && (
            <span>
              <Button variant='secondary' iconName='collapse-left-small' onClick={toggleNav} />
            </span>
          )}
        </Heading>
        <NakedUl css={listStyles}>
          {subNavItems.map(({ label, count, isPassiveCount, relativePath, trackingId, path }) => {
            const isActive =
              hasSameRootPath(selectedParent, parentLink) && hasSameSubPath(currentPath, relativePath || path || '');

            return (
              <SubnavItem
                key={relativePath || path}
                label={label}
                count={count}
                isPassiveCount={isPassiveCount}
                relativePath={relativePath}
                path={path}
                trackingId={trackingId}
                parentLink={parentLink}
                isActive={isActive}
                onNavigate={handleNavigate}
              />
            );
          })}
        </NakedUl>
      </div>
      <footer css={{ marginBottom: theme.spacing(1), width: '100%' }}>
        {settings && settings.show !== false && (
          <SettingsNavButton label={settings.label} path={settings.path as StaticSettingsRoutes} />
        )}
      </footer>
    </div>
  );
});

SubmenuContent.displayName = 'SubmenuContent';

const MobileNav = memo(
  forwardRef<HTMLElement, { children: React.ReactNode }>(({ children }, ref) => {
    const { colorObject } = useShellTheme('colorObject');
    const open = useNav((state) => state.open);
    const setOpen = useNav((state) => state.setOpen);
    const [isSubMenu, setIsSubMenu] = useState(true);
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') setOpen(false);
      },
      [setOpen]
    );

    useEffect(() => {
      setIsSubMenu(true);
    }, [open]);

    return (
      <>
        <nav ref={ref} className='mobile-nav' onKeyDown={handleKeyDown} css={mobileNavStyles(open, isSubMenu)}>
          <div css={mobileNavContentStyles}>
            <BubbleNav />
            <NakedUl
              className='wv-nav-main__desktop__mobile'
              tabIndex={0}
              css={[mobileNavListStyles(colorObject.sideNavColor), css({ display: 'none' })]}
            >
              {children}
            </NakedUl>
          </div>
        </nav>
        {open && <Backdrop />}
      </>
    );
  })
);

MobileNav.displayName = 'MobileNav';

const DesktopNav = memo(
  forwardRef<HTMLElement, { children: React.ReactNode }>(({ children }, ref) => {
    const { colorObject } = useShellTheme('colorObject');
    const isSlimModeActive = useNav((state) => state.isSlimModeActive);
    const open = useNav((state) => state.open);
    const setOpen = useNav((state) => state.setOpen);
    const parentLink = useNav((state) => state.parentLink);
    const currentPathname = useNav((state) => state.currentPathname);
    const setCurrentParent = useNav((state) => state.setCurrentParent);
    const subMenuRef = useNav((state) => state.subMenuRef);

    const openStyle = isSlimModeActive ? slimOpenStyle({ open: open || false }) : fullOpenStyle;

    const handleCloseNav = useCallback(() => setOpen(false), [setOpen]);

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        const target = e.target as HTMLElement;
        const path = target.dataset.path;
        if (path) setCurrentParent(path);
      },
      [setCurrentParent]
    );

    const handleMouseLeave = useCallback(() => {
      setCurrentParent('/' + currentPathname.split('/')[1]);
    }, [currentPathname, setCurrentParent]);

    useLayoutEffect(() => {
      const unsubscribe = usePanelStore.subscribe((state, prevState) => {
        if (state.navState !== prevState.navState && state.navState === 'slim' && open) {
          setOpen(false);
        }
      });

      return () => unsubscribe();
    }, [open, setOpen]);

    return (
      <>
        <nav
          ref={ref}
          className='desktop-nav'
          onMouseLeave={handleMouseLeave}
          css={desktopNavStyles(isSlimModeActive, parentLink, colorObject)}
        >
          <div
            css={[
              { display: 'flex', justifyContent: 'space-between', flexDirection: 'column' },
              isSlimModeActive && { position: 'relative', zIndex: 30 },
            ]}
          >
            <MotionNakedUl
              className='wv-nav-main__desktop'
              tabIndex={0}
              initial={{ background: colorObject.sideNavColor }}
              animate={{ background: colorObject.sideNavColor }}
              transition={{ duration: 0.3 }}
              id='desktop-main-nav-items-wrapper'
              css={desktopNavListStyles(isSlimModeActive, open)}
            >
              {children}
            </MotionNakedUl>
            <footer>
              <OnboardingGuide />
              <PendoTrigger />
            </footer>
          </div>
          <section
            ref={subMenuRef}
            tabIndex={0}
            data-path={parentLink}
            onMouseEnter={handleMouseEnter}
            className='wv-nav-subnav-flyout__desktop'
            css={[subnavFlyoutBaseStyles, openStyle]}
          >
            {isSlimModeActive && !open && <ExpandButton onClick={() => setOpen(true)} />}
            <SubmenuContent />
          </section>
        </nav>
        {isSlimModeActive && open && <Backdrop onClick={handleCloseNav} />}
      </>
    );
  })
);

DesktopNav.displayName = 'DesktopNav';

function NavWithoutRef({ children }: NavProps, ref: React.ForwardedRef<HTMLElement>) {
  const navSize = useNavSize();
  const drawerModeActive = navSize.isLte('xsmall');

  return drawerModeActive ? <MobileNav ref={ref}>{children}</MobileNav> : <DesktopNav ref={ref}>{children}</DesktopNav>;
}

const NavWithRef = forwardRef(NavWithoutRef);

export const Nav = NavWithRef;
